<template>
  <div>
  <v-data-table
    item-key="job_id"
    :headers="headers"
    :items="baseJobs"
    :loading="loading"
    :options.sync="options"
    :server-items-length="itemsLength"
    :footer-props="$defaultDataTableFooterProps"
    width="100%"
    class="elevation-1"
    :expanded.sync="expanded"
    :item-class="getRowClass"
    show-expand
    single-expand
  >
    <template v-slot:expanded-item="{ headers, item }">
      <td :colspan="headers.length" class="subtasks-container pa-0">
        <!-- Subtasks Header -->
        <div class="subtasks-header">
          <div class="subtask-header-cell">{{ $t('subtask') }}</div>
          <div class="subtask-header-cell">{{ $t('created_by') }}</div>
          <div class="subtask-header-cell">{{ $t('current_pool') }}</div>
          <div class="subtask-header-cell">{{ $t('current_lane') }}</div>
          <div class="subtask-header-cell">{{ $t('subtask_status') }}</div>
          <div class="subtask-header-cell">{{ $t('subtask_start') }}</div>
          <div class="subtask-header-cell">{{ $t('subtask_end') }}</div>
          <div class="subtask-header-cell subtask-actions">{{ $t('actions') }}</div>
        </div>
        <!-- Subtasks Rows -->
        <div
          v-for="subtask in getSubtasks(item.job_id)"
          :key="subtask.job_id"
          class="subtask-row"
        >
          <div class="subtask-cell">
            <v-icon left size="18">{{ getJobDefinitionById(subtask.job_definition_id)?.icon }}</v-icon>
            {{ subtask.job_title + " - " + item.job_title }}

            <v-chip
              v-if="subtask.readonly"
              class="ml-1"
              color="success"
              small
            >{{ $t('finished') }}</v-chip>
          </div>
          <div class="subtask-cell">
            <UserInfoColumn :user="getUser(subtask.users_id)" />
          </div>
          <div class="subtask-cell">
            {{ getPoolTitle(subtask.lane_id) }}
          </div>
          <div class="subtask-cell">
            {{ getLaneTitle(subtask.lane_id) }}
          </div>
          <div class="subtask-cell">
            <v-chip
              :color="getJobPunctualityColor(subtask)"
              outlined
              small
            >{{ getStatusTitle(subtask.status_id) }}</v-chip>
          </div>
          <div class="subtask-cell">
            {{ formatDate(subtask.start_date) }}
          </div>
          <div class="subtask-cell">
            {{ formatDate(subtask.end_date) }}
          </div>
          <div class="subtask-cell subtask-actions">
            <v-menu v-if="!isPoolReadOnly(subtask)" offset-y>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon>mdi-dots-vertical</v-icon>
                </v-btn>
              </template>
              <v-list>
                <v-list-item @click="viewJob(subtask)">
                  <v-list-item-icon>
                    <v-icon>mdi-open-in-new</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>{{ $t('open_subtask') }}</v-list-item-title>
                </v-list-item>
                <v-list-item
                  v-if="canAdvanceJob(subtask) && !subtask.readonly"
                  @click="openMoveToLane(subtask)"
                >
                  <v-list-item-icon>
                    <v-icon>mdi-send-variant-outline</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>{{ $t('send_subtask') }}</v-list-item-title>
                </v-list-item>
                <v-list-item
                  v-if="canAdvanceJob(subtask) && !subtask.readonly"
                  @click="openFinishSubtaskDialog(subtask)"
                >
                  <v-list-item-icon>
                    <v-icon>mdi-checkbox-marked-outline</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>{{ $t('finish_subtask') }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
        </div>
      </td>
    </template>

    <template v-slot:item.data-table-expand="{ item, isExpanded, expand }">
      <v-btn
        v-if="hasSubtasks(item.job_id)"
        icon
        @click="expand(!isExpanded)"
        :class="{ 'expanded-row': isExpanded }"
      >
        <v-icon>{{ isExpanded ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
      </v-btn>
    </template>

    <template v-slot:item.fields.job_title="{ item }">
      <span>
        <v-icon left size="18">{{ getJobDefinitionById(item.job_definition_id)?.icon }}</v-icon>
        {{ item.job_title }}

        <v-chip
          v-if="item.readonly"
          class="ml-1"
          color="success"
          small
        >{{ $t('finished') }}</v-chip>
      </span>
    </template>

    <template v-slot:item.fields.job_id="{ item }">
      {{ item.job_id }}
    </template>

    <template v-slot:item.fields.customer_title="{ item }">
      {{ getCustomerTitle(item) }}
    </template>

    <template v-slot:item.fields.campaign_title="{ item }">
      {{ getCampaignTitle(item) }}
    </template>

    <template v-slot:item.fields.job_customer_code="{ item }">
      {{ getJobCustomerCode(item) }}
    </template>

    <template v-slot:item.fields.submitter_name="{ item }">
      <UserInfoColumn :user="getUser(item.users_id)" />
    </template>

    <template v-slot:item.fields.pool_title="{ item }">
      {{ getPoolTitle(item.lane_id) }}
    </template>

    <template v-slot:item.deliverables="{ item }">
      <div class="deliverables-cell">
        {{ getDeliverablesCount(item.deliverables) }}
      </div>
    </template>

    <template v-slot:item.fields.lane_title="{ item }">
      {{ getLaneTitle(item.lane_id) }}
    </template>

    <template v-slot:item.fields.status_title="{ item }">
      <v-chip
        :color="getJobPunctualityColor(item)"
        outlined
        small
      >{{ getStatusTitle(item.status_id) }}</v-chip>
    </template>

    <template v-slot:item.fields.deadline="{ item }">
      {{ getFormattedDeadline(item.deadline) }}
    </template>

    <template v-slot:item.actions="{ item }">
      <v-menu v-if="!isPoolReadOnly(item)" offset-y>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            icon
            v-bind="attrs"
            v-on="on"
          >
            <v-icon>mdi-dots-vertical</v-icon>
          </v-btn>
        </template>
        <v-list>
          <v-list-item @click="viewJob(item)">
            <v-list-item-icon>
              <v-icon>mdi-open-in-new</v-icon>
            </v-list-item-icon>
            <v-list-item-title>{{ $t('open_job') }}</v-list-item-title>
          </v-list-item>
          <v-list-item
            v-if="item.job_folder_id"
            @click="openJobFilesFolder(item)"
          >
            <v-list-item-icon>
              <v-icon>mdi-folder-open-outline</v-icon>
            </v-list-item-icon>
            <v-list-item-title>{{ $t('open_job_folder') }}</v-list-item-title>
          </v-list-item>
          <v-list-item
            v-if="canManageDeliverables(item)"
            @click="openEditDeliverables(item)"
          >
            <v-list-item-icon>
              <v-icon>mdi-package-variant</v-icon>
            </v-list-item-icon>
            <v-list-item-title>{{ $t('manage_deliverables') }}</v-list-item-title>
          </v-list-item>
          <v-list-item
            v-if="canManageDeliverables(item)"
            @click="openDeliverableAssets(item)"
          >
            <v-list-item-icon>
              <v-icon>mdi-image-multiple-outline</v-icon>
            </v-list-item-icon>
            <v-list-item-title>{{ $t('deliverable_assets') }}</v-list-item-title>
          </v-list-item>

          <v-list-item
            v-if="canAdvanceJob(item)"
            @click="openMoveToLane(item)"
          >
            <v-list-item-icon>
              <v-icon>mdi-send-variant-outline</v-icon>
            </v-list-item-icon>
            <v-list-item-title>{{ $t('send_job') }}</v-list-item-title>
          </v-list-item>
          <v-list-item
            v-if="canCreateSubtask(item)"
            @click="openCreateSubtaskDialog(item)"
          >
            <v-list-item-icon>
              <v-icon>mdi-content-copy</v-icon>
            </v-list-item-icon>
            <v-list-item-title>{{ $t('create_subtask') }}</v-list-item-title>
          </v-list-item>
          <v-list-item
            v-if="canFinishJob(item) && !item.readonly"
            @click="openFinishJobDialog(item)"
          >
            <v-list-item-icon>
              <v-icon>mdi-checkbox-marked-outline</v-icon>
            </v-list-item-icon>
            <v-list-item-title>{{ $t('finish_job') }}</v-list-item-title>
          </v-list-item>
          <v-list-item
            v-if="canReopenJob(item)"
            @click="handleReopenJob(item)"
          >
            <v-list-item-icon>
              <v-icon>mdi-lock-open-variant-outline</v-icon>
            </v-list-item-icon>
            <v-list-item-title>{{ $t('reopen_job') }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </template>

  </v-data-table>

  <MoveToLaneDialog
    v-model="showMoveDialog"
    :job="moveJobContext"
    :pools="allowedPools"
    :lanes="lanes"
    :user-lanes="userLanes"
    :statuses="statuses"
    :groups="groups"
    :initial-selection="moveInitialSelection"
    @confirm="handleMoveConfirm"
    style="z-index: 10002;"
  />

  <!-- Create Subtask Dialog -->
  <MoveToLaneDialog
    v-model="showCreateSubtaskDialog"
    :pools="allowedPools"
    :statuses="statuses"
    :lanes="lanes"
    :user-lanes="userLanes"
    :groups="groups"
    :job="createSubtaskJobContext"
    mode="create_subtask"
    @confirm="handleCreateSubtaskConfirm"
    style="z-index: 10002;"
  />

  <!-- Finish Subtask Confirmation Dialog -->
  <v-dialog v-model="showFinishSubtaskDialog" max-width="500px" persistent>
    <v-card>
      <v-card-title class="headline">
        {{ $t('finish_subtask') }}
      </v-card-title>

      <v-card-text v-if="finishSubtaskJobContext"
        v-html="$t('confirm_finish_subtask_message', { name: finishSubtaskJobContext.job_title })"
      >
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          color="grey darken-1"
          text
          @click="cancelFinishSubtask"
        >
          {{ $t('cancel') }}
        </v-btn>
        <v-btn
          color="primary"
          @click="confirmFinishSubtask"
          text
        >
          {{ $t('confirm') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Finish Job Confirmation Dialog -->
  <v-dialog v-model="showFinishJobDialog" max-width="500px" persistent>
    <v-card>
      <v-card-title class="headline">
        {{ $t('finish_job') }}
      </v-card-title>

      <v-card-text v-if="finishJobJobContext"
        v-html="$t('confirm_finish_job_message', { name: finishJobJobContext.job_title })"
      >
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          color="grey darken-1"
          text
          @click="cancelFinishJob"
        >
          {{ $t('cancel') }}
        </v-btn>
        <v-btn
          color="primary"
          @click="confirmFinishJob"
          text
        >
          {{ $t('confirm') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
  </div>
</template>

<script>
import { format, parseISO } from 'date-fns'
import { enUS, ptBR } from 'date-fns/locale'
import { mapState, mapActions, mapMutations } from 'vuex'

import NotificationActions from '@/store/core/notification/actions-types'
import JobActions from '@/store/job/actions-types'
import JobMutations from '@/store/job/mutations-types'

import JobAPI from '@/services/api/job/job'

import UserInfoColumn from '@/components/content/navigation/list/UserInfoColumn'
import MoveToLaneDialog from '@/components/job/MoveToLaneDialog.vue'

export default {
  name: "JobListView",

  components: {
    UserInfoColumn,
    MoveToLaneDialog,
  },

  props: {
    groups: {
      type: Array,
      default: () => [],
    },
    users: {
      type: Array,
      default: () => [],
    },
    statuses: {
      type: Array,
      default: () => []
    },
    lanes: {
      type: Array,
      default: () => []
    },
    userLanes: {
      type: Array,
      default: () => []
    },
    pools: {
      type: Array,
      default: () => []
    },
    jobDefinitions: {
      type: Array,
      default: () => []
    },
    campaigns: {
      type: Array,
      default: () => []
    },
    customers: {
      type: Array,
      default: () => []
    },
    userAcls: {
      type: Array,
      default: () => []
    },
    appConfig: {
      type: Object,
      default: () => {},
    },
    filterCampaign: {
      type: Array,
      default: () => []
    },
    filterCooperative: {
      type: Array,
      default: () => []
    },
    filterCustomer: {
      type: Array,
      default: () => []
    },
    filterRequester: {
      type: Array,
      default: () => []
    },
    filterPool: {
      type: Array,
      default: () => []
    },
    filterLane: {
      type: Array,
      default: () => []
    },
    filterJobCustomerCode: {
      type: String,
      default: ''
    },
    filterStatus: {
      type: Array,
      default: () => []
    },
    filterPunctuality: {
      type: Array,
      default: () => []
    },
    filterShowFinished: {
      type: Boolean,
      default: false
    },
    searchJobEvent: {
      type: String,
      default: null
    },
  },

  data() {
    return {
      loading: false,
      itemsLength: 0,
      reloadRequestId: 0,
      minSearchLength: 3,
      options: {},
      showMoveDialog: false,
      moveJobContext: null,
      expanded: [],
      showCreateSubtaskDialog: false,
      createSubtaskJobContext: null,
      showFinishSubtaskDialog: false,
      finishSubtaskJobContext: null,
      showFinishJobDialog: false,
      finishJobJobContext: null,
    }
  },

  mounted() {
    //this.fetchData()
  },

  watch: {
    options: {
      handler () {
        this.reloadJobs()
      },
      deep: true,
    },
    filterCampaign: {
      handler () {
        this.resetPageAndReload()
      },
      deep: true,
    },
    filterCooperative: {
      handler () {
        this.resetPageAndReload()
      },
      deep: true,
    },
    filterCustomer: {
      handler () {
        this.resetPageAndReload()
      },
      deep: true,
    },
    filterRequester: {
      handler () {
        this.resetPageAndReload()
      },
      deep: true,
    },
    filterPool: {
      handler () {
        this.resetPageAndReload()
      },
      deep: true,
    },
    filterLane: {
      handler () {
        this.resetPageAndReload()
      },
      deep: true,
    },
    filterJobCustomerCode() {
      this.resetPageAndReload()
    },
    filterStatus: {
      handler () {
        this.resetPageAndReload()
      },
      deep: true,
    },
    filterPunctuality: {
      handler () {
        this.resetPageAndReload()
      },
      deep: true,
    },
    filterShowFinished() {
      this.resetPageAndReload()
    },
    searchJobEvent: {
      handler () {
        this.resetPageAndReload()
      },
      deep: true,
    },
  },


  computed: {
    ...mapState({
      userData: state => state.core.auth.userData,
      jobs: state => state.job.jobs,
    }),

    baseJobs() {
      // Filter out subtasks - only show base jobs (jobs without parent_job_id)
      return (this.jobs || []).filter(job => !job.parent_job_id)
    },

    subtasks() {
      // Get all subtasks
      return (this.jobs || []).filter(job => job.parent_job_id)
    },

    headers() {
      return [
        {text: this.$t('Id'), value: 'fields.job_id'},
        {text: this.$t('title'), value: 'fields.job_title', width: '25%', class: 'job-title-column', cellClass: 'job-title-column'},
        {text: this.$t('customer'), value: 'fields.customer_title'},
        {text: this.$t('campaign'), value: 'fields.campaign_title'},
        {text: this.$t('job_customer_code'), value: 'fields.job_customer_code'},
        {text: this.$t('submitter'), value: 'fields.submitter_name'},
        {text: this.$t('deliverables'), sortable: false, value: 'deliverables'},
        {text: this.$t('current_pool'), value: 'fields.pool_title'},
        {text: this.$t('current_lane'), value: 'fields.lane_title'},
        {text: this.$t('current_status'), value: 'fields.status_title'},
        {text: this.$t('deadline'), value: 'fields.deadline'},
        {text: this.$t('actions'), value: 'actions', sortable: false, align: 'center'},
      ]
    },

    currentLocale() {
      const locale = this.$i18n.locale

      const localeMap = {
        'en': enUS,
        'pt': ptBR,
        'pt-BR': ptBR
      }
      return localeMap[locale] || enUS
    },

    allowedPools() {
      const MOVE_ACL = 1
      const allowedIds = (this.userAcls || [])
        .filter(acl => acl.acl & MOVE_ACL)
        .map(acl => acl.pool_id)
      return (this.pools || []).filter(p => allowedIds.includes(p.pool_id))
    },

    moveInitialSelection() {
      const job = this.moveJobContext
      if (!job) {
        return { poolId: null, statusId: null, laneId: null, slaHours: null, sumSLA: false }
      }

      const currentLane = (this.lanes || []).find(l => (l.lane_id || l.id) == job.lane_id) || null
      const currentPoolId = currentLane?.pool?.pool_id || null
      const allowedPoolIds = (this.allowedPools || []).map(p => p.pool_id)
      const selectedPoolId = allowedPoolIds.includes(currentPoolId)
        ? currentPoolId
        : ((this.allowedPools[0] && this.allowedPools[0].pool_id) || null)

      return {
        poolId: selectedPoolId,
        statusId: null,
        laneId: null,
        slaHours: null,
        sumSLA: false,
      }
    },
  },

  methods: {
    ...mapActions('job', [
      JobActions.GET_ALL_JOBS,
      JobActions.SEARCH_JOB,
      JobActions.MOVE_TO_POOL,
      JobActions.CREATE_SUBTASK,
      JobActions.FINISH_SUBTASK,
      JobActions.FINISH_JOB,
      JobActions.REOPEN_JOB,
    ]),

    ...mapActions('core/notification', [
      NotificationActions.SEND_MESSAGE,
    ]),

    ...mapMutations('job', [
      JobMutations.SET_JOBS,
      JobMutations.REMOVE_SUBTASKS_BY_PARENT_JOB_ID,
      JobMutations.REMOVE_JOB,
    ]),

    reloadJobs() {
      if (this.searchJobEvent) {
        this.searchJobByTerm()

        return
      }

      this.fetchData()
    },

    resetPageAndReload() {
      // If page is not already at the first one, changing it will trigger
      // the `options` watcher (deep) which in turn calls reloadJobs().
      // Only call reloadJobs() directly when the page was already 1,
      // otherwise we'd end up fetching twice.
      if (this.options.page !== 1) {
        this.options.page = 1

        return
      }

      this.reloadJobs()
    },


    fetchData() {
      this.loading = true

      const requestId = ++this.reloadRequestId
      const options = {
        ...this.buildJobListOptions(),
        // The component commits only the newest response, preventing an older
        // request from replacing the list after a filter has changed.
        skipJobsCommit: true,
      }

      return this[JobActions.GET_ALL_JOBS](options)
        .then((result) => {
          if (requestId !== this.reloadRequestId) {
            return
          }

          this.itemsLength = result.total
          this[JobMutations.SET_JOBS](result.items)
        })
        .finally(() => {
          if (requestId === this.reloadRequestId) {
            this.loading = false
          }
        })
    },

    searchJobByTerm() {
      if (! this.searchJobEvent) {
        this.fetchData()

        return
      }

      this.loading = true

      const requestId = ++this.reloadRequestId
      const options = {
        ...this.buildJobListOptions(),
        text: this.searchJobEvent,
      }

      return this[JobActions.SEARCH_JOB](options)
        .then((result) => {
          // Ignore stale responses so a slower/earlier request can't
          // overwrite the results of a newer search/filter.
          if (requestId !== this.reloadRequestId) {
            return
          }

          this.itemsLength = result.total

          this[JobMutations.SET_JOBS](result.items)
        })
        .finally(() => {
          if (requestId === this.reloadRequestId) {
            this.loading = false
          }
        })
    },

    buildJobListOptions() {
      const options = {
        ...this.buildJobFilterOptions(),
        order: 'created_date',
        direction: 'DESC',
      }

      if (this.options.sortBy.length > 0) {
        options.order = this.options.sortBy[0]
      }

      if (this.options.sortBy.length > 0) {
        options.direction = this.options.sortDesc[0] ? 'DESC' : 'ASC'
      }

      options.limit = this.options.itemsPerPage
      options.offset = this.options.page - 1

      return options
    },

    removeSubtasksForTerminalParent(job, statusId) {
      if (job?.parent_job_id) return false

      const status = this.statuses.find(item => item.status_id == statusId)
      if (!['done', 'canceled'].includes(status?.status_type)) return false

      this[JobMutations.REMOVE_SUBTASKS_BY_PARENT_JOB_ID](job.job_id)
      return true
    },

    buildJobFilterOptions() {
      const options = {
        ...this.$route.query,
        'filter[removeViewOnly]': true,
        'filter[subtask]': true,
        'filter[nodeType]': 'job_instance',
      }

      delete options.customers
      delete options.requesters
      delete options.pools
      delete options.lanes
      delete options.jobCustomerCode
      delete options.campaigns
      delete options.statuses
      delete options.punctuality
      delete options.view
      delete options.timelineInterval

      if (this.filterCampaign.length > 0) {
        options['filter[campaignId]'] = this.filterCampaign.join(',')
      }

      const customerFilter = this.filterCustomer.length > 0
        ? this.filterCustomer
        : this.filterCooperative

      if (customerFilter.length > 0) {
        options['filter[customerId]'] = customerFilter.join(',')
      }

      if (this.filterRequester.length > 0) {
        options['filter[requesterUsersId]'] = this.filterRequester.join(',')
      }

      if (this.filterPool.length > 0) {
        options['filter[poolId]'] = this.filterPool.join(',')
      }

      if (this.filterLane.length > 0) {
        options['filter[laneId]'] = this.filterLane.join(',')
      }

      if (this.filterJobCustomerCode) {
        options['filter[job_customer_code]'] = this.filterJobCustomerCode
      }

      if (this.filterStatus.length > 0) {
        options['filter[statusId]'] = this.filterStatus.join(',')
      }

      if (this.filterPunctuality.length > 0) {
        options['filter[punctuality]'] = this.filterPunctuality.join(',')
      }

      if (this.filterShowFinished) {
        options['filter[readonly]'] = true
      }

      return options
    },

    getFormattedDeadline(deadline) {
      if (!deadline) {
        return '—'
      }

      if (this.isDateTimeString(deadline)) {
        deadline = this.parseJobDate(deadline)
      } else {
        deadline = parseISO(deadline)
      }

      return format(deadline, 'dd/MM/yyyy')
    },

    isDateTimeString(dateString) {
      const timeIndicators = /[T\s]\d{2}:\d{2}(:\d{2}(\.\d{3})?)?/; // T or space, HH:MM, optional :SS, optional .SSS
      return timeIndicators.test(dateString);
    },

    formatDate(dateString) {
      if (!dateString) return ''

      try {
        // Handle the format: 2025-09-15 10:00:00+0000
        const parsedDate = this.parseJobDate(dateString)
        return format(parsedDate, 'dd/MM/yyyy HH:mm', { locale: this.currentLocale })
      } catch (error) {
        console.warn('Error formatting job time:', error, dateString)
        return dateString
      }
    },

    parseJobDate(dateString) {
      if (!dateString) return new Date()

      try {
        // First try standard ISO format
        if (dateString.includes('T')) {
          return parseISO(dateString)
        }

        // Handle format: 2025-09-15 10:00:00+0000
        // This is actually Brazil local time, not UTC despite the +0000
        // Extract date and time parts
        const [datePart, timePart] = dateString.split(' ')
        const [year, month, day] = datePart.split('-').map(Number)
        const [hour, minute, second] = timePart.replace(/\+\d{4}$/, '').split(':').map(Number)

        // Create date in local timezone (Brazil)
        return new Date(year, month - 1, day, hour, minute, second || 0)
      } catch (error) {
        console.warn('Error parsing job date:', error, dateString)
        // Fallback: try to parse as regular date
        return new Date(dateString)
      }
    },

    getJobPunctualityColor(job) {
      const status = this.getStatusById(job.status_id)

      // Removendo timezone offset das strings de data
      const jobStartDate = new Date(job.start_date?.replace(/\+\d{4}$/, ""))
      const jobEndDate = new Date(job.end_date?.replace(/\+\d{4}$/, ""))
      const jobDeadline = new Date(job.deadline?.replace(/\+\d{4}$/, ""))

      const currentTime = new Date()

      if (job.view_only) {
        return '#78828c'
      }

      if (status.status_type == "waiting" && jobStartDate > currentTime) {
        return '#b0b5bf'
      }

      if (status.status_type == "done" || true == job.readonly) {
        return '#9bd5b7'
      }

      if (status.status_type == "canceled") {
        return '#ab8cf1'
      }

      if (status.status_type == "doing" && jobEndDate > currentTime) {
        return '#f5ba54'
      }

      if (
        status.status_type == "waiting"
        && jobStartDate <= currentTime
        && jobEndDate > currentTime
      ) {
        return '#f99d6d'
      }

      if (
        (status.status_type == "doing" || status.status_type == "waiting")
        && jobDeadline <= currentTime
        && job.deadline
      ) {
        return '#ef898a'
      }

      if (
        (status.status_type == "doing" || status.status_type == "waiting")
        && jobEndDate <= currentTime
      ) {
        return '#e45957'
      }

      return ''
    },

    getJobDefinitionById(id) {
      return this.jobDefinitions.find(jb => jb.job_definition_id == id)
    },

    getStatusById(statusId) {
      return this.statuses.find(s => s.status_id == statusId)
    },

    getUser(usersId) {
      return this.users.find(user => user.users_id == usersId)
    },

    getJobField(job, fieldName) {
      return job?.[fieldName] ?? job?.fields?.[fieldName] ?? null
    },

    getCustomerId(job) {
      return this.getJobField(job, 'customer_id') ?? job?.fields?.cooperada ?? null
    },

    getCustomerTitle(job) {
      const customerId = this.getCustomerId(job)
      const fieldTitle = this.getJobField(job, 'customer_title') ?? this.getJobField(job, 'customer_name')

      if (fieldTitle) {
        return fieldTitle
      }

      const customer = this.customers.find(customer => customer.customer_id == customerId)

      return customer?.customer_name || customerId || '—'
    },

    getCampaignTitle(job) {
      const campaignId = this.getJobField(job, 'campaign_id')
      const fieldTitle = this.getJobField(job, 'campaign_title')

      if (fieldTitle) {
        return fieldTitle
      }

      const campaign = this.campaigns.find(campaign => campaign.campaign_id == campaignId)

      return campaign?.campaign_title || campaignId || '—'
    },

    getJobCustomerCode(job) {
      return this.getJobField(job, 'job_customer_code') || '—'
    },

    getPoolTitle(laneId) {
      return this.lanes.find(lane => lane.lane_id == laneId)?.pool.pool_title
    },

    getLaneTitle(laneId) {
      return this.lanes.find(lane => lane.lane_id == laneId)?.lane_title
    },

    getStatusTitle(statusId) {
      return this.statuses.find(status => status.status_id == statusId)?.status_title
    },

    getDeliverablesCount(deliverables) {
      if (!deliverables || deliverables?.length == 0) {
        return '—'
      }

      return deliverables.reduce((accumulator, deliverable) => {
        return accumulator + (deliverable.quantity || 1)
      }, 0)
    },

    hasSubtasks(jobId) {
      return this.subtasks.some(subtask => subtask.parent_job_id === jobId)
    },

    getSubtasks(jobId) {
      return this.subtasks.filter(subtask => subtask.parent_job_id === jobId)
    },

    isJobExpanded(item) {
      return this.expanded.some(expandedItem => expandedItem.job_id === item.job_id)
    },

    getRowClass(item) {
      return this.isJobExpanded(item) ? 'expanded-row-bg' : ''
    },

    openMoveToLane(job) {
      if (!this.canAdvanceJob(job)) return

      this.moveJobContext = job
      this.showMoveDialog = true
    },

    isPoolReadOnly(job) {
      const lane = this.lanes.find(lane => lane.lane_id == job?.lane_id)
      const poolId = lane?.pool?.pool_id ?? lane?.pool_id
      if (poolId == null) return false

      // Combine direct and group permissions only for this job's Pool.
      const poolAcl = this.userAcls
        .filter(acl => acl.pool_id == poolId)
        .reduce((permissions, acl) => permissions | Number(acl.acl), 0)

      return poolAcl === 2048
    },

    viewJob(job) {
      this.$emit('view-job', job.job_id, this.isPoolReadOnly(job) ? 'consult' : undefined)
    },

    getJobMode(job) {
      if (this.canInteractWithJob(job)) {
        return 'update'
      }

      return 'consult'
    },

    canInteractWithJob(job) {
      if (!job || job.view_only || job.readonly || this.isPoolReadOnly(job)) {
        return false
      }

      return this.userLanes.includes(job.lane_id)
    },

    canAdvanceJob(job) {
      if (this.getJobMode(job) === 'consult') return false
      const MOVE_ACL = 1
      const hasMoveAcl = (this.userAcls || []).some(acl => acl.acl & MOVE_ACL)
      return this.canInteractWithJob(job) && hasMoveAcl && (this.allowedPools?.length > 0)
    },

    canCreateSubtask(job) {
      // ACL 32 is required to create subtasks
      const CREATE_SUBTASK_ACL = 32

      // Closed jobs are read-only and cannot receive new subtasks
      if (!job || job.readonly || this.isPoolReadOnly(job)) {
        return false
      }

      // Only allow creating subtasks for base jobs (job_instance), not for subtasks themselves
      if (!job || job.node_type !== 'job_instance') {
        return false
      }

      // Check if user has the CREATE_SUBTASK_ACL permission in the job's pool
      const jobLane = this.lanes.find(l => l.lane_id === job.lane_id)
      if (!jobLane) {
        return false
      }

      const poolId = jobLane.pool?.pool_id
      if (!poolId) {
        return false
      }

      return this.userAcls.some(acl =>
        acl.pool_id === poolId && (acl.acl & CREATE_SUBTASK_ACL) !== 0
      )
    },

    canFinishJob(job) {
      // ACL 1024 is required to finish a main job
      const FINISH_JOB_ACL = 1024

      // Only allow finishing base jobs (job_instance), not subtasks
      if (!job || job.node_type !== 'job_instance') {
        return false
      }

      if (!this.canInteractWithJob(job)) {
        return false
      }

      const jobLane = this.lanes.find(l => l.lane_id === job.lane_id)
      if (!jobLane) {
        return false
      }

      const poolId = jobLane.pool?.pool_id
      if (!poolId) {
        return false
      }

      return this.userAcls.some(acl =>
        acl.pool_id === poolId && (acl.acl & FINISH_JOB_ACL) !== 0
      )
    },

    canReopenJob(job) {
      const FINISH_JOB_ACL = 1024

      if (this.isPoolReadOnly(job) || !this.filterShowFinished || !job?.readonly || job.parent_job_id || job.node_type !== 'job_instance') {
        return false
      }

      const jobLane = this.lanes.find(l => l.lane_id === job.lane_id)
      const poolId = jobLane?.pool?.pool_id

      return !!poolId && this.userAcls.some(acl =>
        acl.pool_id === poolId && (acl.acl & FINISH_JOB_ACL) !== 0
      )
    },

    canManageDeliverables(job) {
      // ACL 128 (edit) or 256 (view) is required to manage/view deliverables
      const EDIT_DELIVERABLES_ACL = 128
      const VIEW_DELIVERABLES_ACL = 256

      if (!job || job.readonly || this.isPoolReadOnly(job)) {
        return false
      }

      // Check if user has view or edit deliverables permission in the job's pool
      const jobLane = this.lanes.find(l => l.lane_id === job.lane_id)
      if (!jobLane) {
        return false
      }

      const poolId = jobLane.pool?.pool_id
      if (!poolId) {
        return false
      }

      return this.userAcls.some(acl =>
        acl.pool_id === poolId &&
        ((acl.acl & EDIT_DELIVERABLES_ACL) !== 0 || (acl.acl & VIEW_DELIVERABLES_ACL) !== 0)
      )
    },

    openEditDeliverables(job) {
      if (!this.canManageDeliverables(job)) return

      this.$router.push({
        name: 'JobDeliverableManagement',
        params: {
          appId: this.$route.params.appId,
          jobId: job.job_id,
          businessAppId: this.$route.params.businessAppId
        }
      })
    },

    openDeliverableAssets(job) {
      if (!this.canManageDeliverables(job)) return

      const contentAppId = this.appConfig?.content_app_inst_id || this.appConfig?.content_app_id
      const businessAppId = this.appConfig?.business_app_inst_id || this.appConfig?.business_app_id
      this.$router.push({
        name: 'JobDeliverableAssets',
        params: {
          appId: this.$route.params.appId,
          jobId: job.job_id,
          contentAppId,
          businessAppId
        }
      })
    },

    openJobFilesFolder(job) {
      const contentsId = this.$route.params.contentAppId ||
        this.appConfig?.content_app_inst_id ||
        this.appConfig?.content_app_id || null

      const jobFolderId = job?.job_folder_id

      if (!contentsId || !jobFolderId) return

      const { href } = this.$router.resolve({
        name: 'ContentNavigation',
        params: { appId: contentsId, parentId: jobFolderId },
      })

      window.open(href, '_blank', 'noopener')
    },

    async handleMoveConfirm(payload) {
      if (!this.canAdvanceJob(this.moveJobContext)) return

      try {
        const newLane = this.lanes.find(l => (l.lane_id || l.id) == payload.laneId)
        const params = {
          job_id: this.moveJobContext.job_id,
          pool_id: payload.poolId || (newLane && newLane.pool && newLane.pool.pool_id),
          lane_id: payload.laneId,
          status_id: payload.statusId,
          sum_sla: !!payload.sumSLA,
          ...(Number(payload.slaHours) > 0 ? { sla: Number(payload.slaHours) } : {}),
          pool_title: newLane?.pool?.pool_title,
        }

        this.loading = true

        const moveResult = await this[JobActions.MOVE_TO_POOL](params)
        let moveContext = {
          jobTitle: this.moveJobContext.job_title,
          poolId: params.pool_id
        }

        if (this.moveJobContext.parent_job_id) {
          moveContext.jobTitle = `${this.moveJobContext.job_title} - ${this.moveJobContext?.fields?.parent_job_title}`
        }

        this.notifyJobEnteredLane(moveResult, moveContext)

        this.$nextTick(() => {
          this.$forceUpdate()
        })

        if (!this.removeSubtasksForTerminalParent(this.moveJobContext, params.status_id)) {
          await this.fetchData()
        }
      } finally {
        this.loading = false
        this.showMoveDialog = false
        this.moveJobContext = null
      }
    },

    async notifyJobEnteredLane(result, context = {}) {
      try {
        const recipientsResult = await JobAPI.getCustomerVisibleActors(
          result.job_id,
          result.lane_id
        )
        const usersIds = recipientsResult.data?.users_ids ?? []
        const groupsIds = recipientsResult.data?.user_group_ids ?? []

        const jobTitle = context.jobTitle
        const startDateStr = (result && result.start_date)
        const link = this.buildAgendaLinks(context.startDate || startDateStr)

        const subject = this.$t('job_entered_lane_subject', { jobTitle })
        const content = this.$t('job_entered_lane_content', { jobTitle, link })

        const options = {
          subject,
          content,
          users_ids: usersIds,
          groups_ids: groupsIds,
        }

        this[NotificationActions.SEND_MESSAGE](options)
      } catch (e) {
        console.error(e)
      }
    },

    buildAgendaLinks(dateStr) {
      const appId = this.$route.params.appId
      const contentAppId = this.$route.params.contentAppId || 1
      const route = this.$router.resolve({ name: 'JobHomeContent', params: { appId, contentAppId } })

      const url = new URL(route.href, window.location.origin)
      url.searchParams.set('timelineInterval', '2') // 3 dias
      const dateParam = this.formatDateParamForURL(dateStr)
      if (dateParam) url.searchParams.set('date', dateParam)
      return url.href
    },

    formatDateParamForURL(dateInput) {
      try {
        let d
        if (dateInput instanceof Date) {
          d = dateInput
        } else if (typeof dateInput === 'string') {
          // Try parse using existing parser
          d = this.parseJobDate(dateInput)
        } else {
          d = new Date()
        }
        if (isNaN(d)) return null

        const pad = (n) => (n < 10 ? '0' + n : '' + n)
        const yyyy = d.getFullYear()
        const mm = pad(d.getMonth() + 1)
        const dd = pad(d.getDate())
        const HH = pad(d.getHours())
        const MM = pad(d.getMinutes())
        const SS = pad(d.getSeconds())
        return `${yyyy}/${mm}/${dd}-${HH}:${MM}:${SS}`
      } catch (_) { return null }
    },

    // Create subtask methods
    openCreateSubtaskDialog(job) {
      if (!this.canCreateSubtask(job)) return

      this.createSubtaskJobContext = job
      this.showCreateSubtaskDialog = true
    },

    async handleCreateSubtaskConfirm(payload) {
      if (!this.canCreateSubtask(this.createSubtaskJobContext)) return

      try {
        this.loading = true

        const params = {
          job_id: this.createSubtaskJobContext.job_id,
          name: payload.name,
          pool_id: payload.poolId,
          lane_id: payload.laneId,
          status_id: payload.statusId,
        }

        await this[JobActions.CREATE_SUBTASK](params)

        this.showCreateSubtaskDialog = false
        this.createSubtaskJobContext = null

        // Refresh jobs list
        await this.fetchData()
      } catch (error) {
        console.error('Error creating subtask:', error)
      } finally {
        this.loading = false
      }
    },

    // Finish subtask methods
    openFinishSubtaskDialog(job) {
      if (!this.canAdvanceJob(job)) return

      this.finishSubtaskJobContext = job
      this.showFinishSubtaskDialog = true
    },

    async confirmFinishSubtask() {
      if (!this.canAdvanceJob(this.finishSubtaskJobContext)) return

      try {
        this.loading = true

        const params = {
          name: this.finishSubtaskJobContext.job_title
        }

        await this[JobActions.FINISH_SUBTASK]({
          jobId: this.finishSubtaskJobContext.job_id,
          params
        })

        this.showFinishSubtaskDialog = false
        this.finishSubtaskJobContext = null

        // Refresh jobs list
        await this.fetchData()
      } catch (error) {
        console.error('Error finishing subtask:', error)
      } finally {
        this.loading = false
      }
    },

    cancelFinishSubtask() {
      this.showFinishSubtaskDialog = false
      this.finishSubtaskJobContext = null
    },

    // Finish job methods
    openFinishJobDialog(job) {
      if (!this.canFinishJob(job)) return

      this.finishJobJobContext = job
      this.showFinishJobDialog = true
    },

    async confirmFinishJob() {
      if (!this.canFinishJob(this.finishJobJobContext)) return

      try {
        this.loading = true

        const params = {
          name: this.finishJobJobContext.job_title
        }

        await this[JobActions.FINISH_JOB]({
          jobId: this.finishJobJobContext.job_id,
          params
        })

        this.showFinishJobDialog = false
        this.finishJobJobContext = null

        // The store has already been updated by FINISH_JOB. Avoid querying the
        // index immediately, because it may still return the just-closed subtasks.
        this.$nextTick(() => this.$forceUpdate())
      } catch (error) {
        console.error('Error finishing job:', error)
      } finally {
        this.loading = false
      }
    },

    cancelFinishJob() {
      this.showFinishJobDialog = false
      this.finishJobJobContext = null
    },

    async handleReopenJob(job) {
      if (!this.canReopenJob(job)) return

      try {
        this.loading = true
        await this[JobActions.REOPEN_JOB](job.job_id)
        this[JobMutations.REMOVE_JOB](job.job_id)
      } catch (error) {
        console.error('Error reopening job:', error)
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style>
.deliverables-cell {
  display: flex;
  align-items: center;
  white-space: nowrap;
  min-width: 80px;
}

/* Prioritize the title column: give it a strong minimum width and let it
   grow, so it isn't squeezed by neighboring columns. */
.job-title-column {
  min-width: 220px;
  white-space: nowrap;
}

/* Keep the other data columns compact so they only take the space they need,
   leaving the remaining width for the prioritized title column. */
.v-data-table td:not(.job-title-column),
.v-data-table th:not(.job-title-column) {
  white-space: nowrap;
}

.expanded-row-bg {
  background-color: #c8e6c9 !important;
}

.expanded-row-bg td {
  background-color: #c8e6c9 !important;
}

.subtasks-container {
  background-color: #f5f5f5;
  padding: 0 !important;
}

.subtasks-header {
  display: flex;
  align-items: center;
  margin: 12px 16px 0 16px;
  padding: 8px 16px;
  background-color: #e0e0e0;
  border: 1px solid #d0d0d0;
  border-bottom: none;
  border-radius: 12px 12px 0 0;
  font-weight: 500;
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.6);
}

.subtask-header-cell {
  flex: 1;
  padding: 0 8px;
  display: flex;
  align-items: center;
  text-transform: none;
  letter-spacing: 0.0892857143em;
}

.subtask-header-cell:first-child {
  flex: 1.5;
}

.subtask-row {
  display: flex;
  align-items: center;
  border: 1px solid #d0d0d0;
  border-radius: 0;
  margin: 0 16px;
  padding: 12px 16px;
  background-color: white;
  border-top: none;
}

.subtask-row:first-of-type {
  border-top: 1px solid #e0e0e0;
}

.subtask-row:last-of-type {
  margin-bottom: 8px;
  border-radius: 0 0 12px 12px;
}

.subtask-cell {
  flex: 1;
  padding: 0 8px;
  display: flex;
  align-items: center;
}

.subtask-cell:first-child {
  flex: 1.5;
}

.subtask-actions {
  flex: 0.5;
  justify-content: center;
}
</style>
